import React, { useEffect, useState } from "react";
import { Mail, Phone, Building2, Trash2, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { api, formatDate } from "@/lib/api";
import { PageHeader, StatusBadge } from "@/components/admin/ui";

export default function AdminInquiries() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(null);
  const [filter, setFilter] = useState("all");

  const load = () => api.get("/admin/inquiries").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    await api.patch(`/admin/inquiries/${id}`, { status });
    setItems((arr) => arr.map((i) => (i.id === id ? { ...i, status } : i)));
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this inquiry?")) return;
    await api.delete(`/admin/inquiries/${id}`);
    toast.success("Deleted");
    load();
  };

  const shown = items.filter((i) => filter === "all" || i.status === filter);

  return (
    <>
      <PageHeader title="Consultation Inquiries" subtitle="Submissions from the contact form, stored securely.">
        <select value={filter} onChange={(e) => setFilter(e.target.value)} className="rounded-sm border border-ink/20 bg-white px-3 py-2 font-sans text-sm" data-testid="inquiry-filter">
          <option value="all">All</option>
          <option value="new">New</option>
          <option value="read">Read</option>
          <option value="archived">Archived</option>
        </select>
      </PageHeader>

      <div className="space-y-3">
        {shown.map((i) => (
          <div key={i.id} className="border border-ink/10 bg-cream-100" data-testid={`inquiry-${i.id}`}>
            <button
              onClick={() => { setOpen(open === i.id ? null : i.id); if (i.status === "new") setStatus(i.id, "read"); }}
              className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left"
            >
              <div className="min-w-0">
                <div className="flex items-center gap-3">
                  <span className="font-sans text-sm font-medium text-ink">{i.name}</span>
                  <StatusBadge status={i.status} />
                </div>
                <p className="mt-1 font-sans text-xs text-steel-500">{i.area} · {formatDate(i.created_at)}</p>
              </div>
              <ChevronDown className={`h-4 w-4 shrink-0 text-steel-400 transition-transform ${open === i.id ? "rotate-180" : ""}`} />
            </button>
            {open === i.id && (
              <div className="border-t border-cream-200 px-5 py-5">
                <div className="grid gap-3 sm:grid-cols-3">
                  <a href={`mailto:${i.email}`} className="flex items-center gap-2 font-sans text-sm text-steel-700 hover:text-gold-600"><Mail className="h-4 w-4 text-gold-500" /> {i.email}</a>
                  {i.phone && <a href={`tel:${i.phone}`} className="flex items-center gap-2 font-sans text-sm text-steel-700 hover:text-gold-600"><Phone className="h-4 w-4 text-gold-500" /> {i.phone}</a>}
                  {i.company && <span className="flex items-center gap-2 font-sans text-sm text-steel-700"><Building2 className="h-4 w-4 text-gold-500" /> {i.company}</span>}
                </div>
                <p className="mt-4 whitespace-pre-line border-l-2 border-gold-500/40 pl-4 font-sans text-sm leading-relaxed text-steel-700">{i.message}</p>
                <div className="mt-5 flex items-center gap-3">
                  <button onClick={() => setStatus(i.id, "archived")} className="font-sans text-xs text-steel-600 hover:text-gold-600">Archive</button>
                  <button onClick={() => setStatus(i.id, "new")} className="font-sans text-xs text-steel-600 hover:text-gold-600">Mark unread</button>
                  <button onClick={() => remove(i.id)} className="flex items-center gap-1 font-sans text-xs text-destructive"><Trash2 className="h-3 w-3" /> Delete</button>
                </div>
              </div>
            )}
          </div>
        ))}
        {shown.length === 0 && <p className="border border-dashed border-ink/15 py-16 text-center font-sans text-sm text-steel-400">No inquiries in this view.</p>}
      </div>
    </>
  );
}
